import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { fg } from '@atlaskit/platform-feature-flags';
import { getBitValueFromBool } from '../../../common/utils/helpers';
import { V001_SCHEMA_KEY, V002_SCHEMA_KEY } from '../../../constants';
import { serializeConsents } from '../../../services/consent-hub-service/utils';
import { ConsentPreferenceV2, PreferenceCategory, PrefetchedPreferenceState } from '../../../types';
import { setCurrentLocaleCookie } from '../set-required-consent-cookies';
export var checkTokenValidity = function checkTokenValidity(consentToken) {
  if (consentToken.length < 3) {
    // Wrong format, no version
    return {
      isValid: false,
      isV001: false,
      isV002: false
    };
  }
  var version = consentToken[0] + consentToken[1] + consentToken[2];
  var isV001 = version === V001_SCHEMA_KEY && consentToken.length === 7;
  var isV002 = version === V002_SCHEMA_KEY && consentToken.length === 12;

  // We support version 001 and 002 only
  var isSupportedVersion = [V001_SCHEMA_KEY, V002_SCHEMA_KEY].includes(version);
  var isValid = isSupportedVersion && (isV001 || isV002);
  return {
    isValid: isValid,
    isV001: isV001,
    isV002: isV002
  };
};

/* Schema Version 001:
---------------------------------------------------
  - Format 0000000
  - 001 (schema version 3 digits)
  - 0/1 (1 digit, FUNCTIONAL cookies disabled/enabled)
  - 0/1 (1 digit, ANALYTICS cookies disabled/enabled)
  - 0/1 (1 digit, MARKETING cookies disabled/enabled)
  - 0/1 (1 digit, UNKNOWN cookies disabled/enabled)
*/

var packPrefsVersion001 = function packPrefsVersion001(userPreferences) {
  var version = V001_SCHEMA_KEY;
  return [version, getBitValueFromBool(userPreferences.FUNCTIONAL), getBitValueFromBool(userPreferences.ANALYTICS), getBitValueFromBool(userPreferences.MARKETING), getBitValueFromBool(userPreferences.UNKNOWN)].join('');
};
var unpackPrefsVersion001 = function unpackPrefsVersion001(userPreferencesCookie) {
  var _ref;
  if (userPreferencesCookie.length !== 7) {
    // Wrong length for version 001
    return;
  }
  return _ref = {}, _defineProperty(_ref, PreferenceCategory.StrictlyNecessary, true), _defineProperty(_ref, PreferenceCategory.Functional, userPreferencesCookie[3] === '1'), _defineProperty(_ref, PreferenceCategory.Analytics, userPreferencesCookie[4] === '1'), _defineProperty(_ref, PreferenceCategory.Marketing, userPreferencesCookie[5] === '1'), _defineProperty(_ref, PreferenceCategory.Unknown, userPreferencesCookie[6] === '1'), _ref;
};

/* Schema Version 002:
---------------------------------------------------
  - Format 000000000000
  - 002 (schema version, 3 digits)
  - 0/1 (1 digit, FUNCTIONAL cookies - disabled/enabled)
  - 0/1 (1 digit, ANALYTICS cookies - disabled/enabled)
  - 0/1 (1 digit, MARKETING cookies - disabled/enabled)
  - 0/1 (1 digit, UNKNOWN cookies - disabled/enabled)
  - 0/1 (1 digit, consent records are unavailable from ConsentHub (logged out) - yes/no)
  - 0/1 (1 digit, consents saved while in GDPR locale (Europe) - yes/no)
  - 0/1 (1 digit, consents saved while in CPRA locale (CPRA will act as 'all USA' bucket) - yes/no)
  - 0/1 (1 digit, consents saved while in LGPD locale (Brazil) - yes/no)
  - 0/1 (1 digit, consents saved while in FADP locale (Switzerland) - yes/no)
*/
var packPrefsVersion002 = function packPrefsVersion002(userPreferences) {
  var version = V002_SCHEMA_KEY;
  return [version, getBitValueFromBool(userPreferences[PreferenceCategory.Functional]), getBitValueFromBool(userPreferences[PreferenceCategory.Analytics]), getBitValueFromBool(userPreferences[PreferenceCategory.Marketing]), getBitValueFromBool(userPreferences[PreferenceCategory.Unknown]), getBitValueFromBool(userPreferences[ConsentPreferenceV2.ConsentDataUnavailable]), getBitValueFromBool(userPreferences[ConsentPreferenceV2.ConsentedUnderGDPR]), getBitValueFromBool(userPreferences[ConsentPreferenceV2.ConsentedUnderCPRA]), getBitValueFromBool(userPreferences[ConsentPreferenceV2.ConsentedUnderLGPD]), getBitValueFromBool(userPreferences[ConsentPreferenceV2.ConsentedUnderFADP])].join('');
};
var unpackPrefsVersion002 = function unpackPrefsVersion002(userPreferencesCookie) {
  var _ref2;
  return _ref2 = {}, _defineProperty(_ref2, PreferenceCategory.StrictlyNecessary, true), _defineProperty(_ref2, PreferenceCategory.Functional, userPreferencesCookie[3] === '1'), _defineProperty(_ref2, PreferenceCategory.Analytics, userPreferencesCookie[4] === '1'), _defineProperty(_ref2, PreferenceCategory.Marketing, userPreferencesCookie[5] === '1'), _defineProperty(_ref2, PreferenceCategory.Unknown, userPreferencesCookie[6] === '1'), _defineProperty(_ref2, ConsentPreferenceV2.ConsentDataUnavailable, userPreferencesCookie[7] === '1'), _defineProperty(_ref2, ConsentPreferenceV2.ConsentedUnderGDPR, userPreferencesCookie[8] === '1'), _defineProperty(_ref2, ConsentPreferenceV2.ConsentedUnderCPRA, userPreferencesCookie[9] === '1'), _defineProperty(_ref2, ConsentPreferenceV2.ConsentedUnderLGPD, userPreferencesCookie[10] === '1'), _defineProperty(_ref2, ConsentPreferenceV2.ConsentedUnderFADP, userPreferencesCookie[11] === '1'), _ref2;
};
var V002SpecificBitKeys = [ConsentPreferenceV2.ConsentDataUnavailable, ConsentPreferenceV2.ConsentedUnderGDPR, ConsentPreferenceV2.ConsentedUnderCPRA, ConsentPreferenceV2.ConsentedUnderLGPD, ConsentPreferenceV2.ConsentedUnderFADP];
export function packUserPreferences(userPreferences) {
  if (!fg('platform.moonjelly.browser-storage-controls-v2')) {
    return packPrefsVersion001(userPreferences);
  }
  var isV002 = V002SpecificBitKeys.every(function (key) {
    return key in userPreferences;
  });
  if (isV002) {
    return packPrefsVersion002(userPreferences);
  }
  // Fallback to V001
  return packPrefsVersion001(userPreferences);
}
export function unpackUserPreferencesCookie(userPreferencesCookie) {
  if (!fg('platform.moonjelly.browser-storage-controls-v2')) {
    if (userPreferencesCookie.length < 3) {
      // Wrong format, no version
      return;
    }
    var version = userPreferencesCookie[0] + userPreferencesCookie[1] + userPreferencesCookie[2];
    if (version === V001_SCHEMA_KEY) {
      return unpackPrefsVersion001(userPreferencesCookie);
    }
    return;
  }
  var _checkTokenValidity = checkTokenValidity(userPreferencesCookie),
    isValid = _checkTokenValidity.isValid,
    isV001 = _checkTokenValidity.isV001,
    isV002 = _checkTokenValidity.isV002;
  if (!isValid) {
    // We support version 001 and 002 only
    return;
  }
  if (isV001) {
    return unpackPrefsVersion001(userPreferencesCookie);
  }
  if (isV002) {
    return unpackPrefsVersion002(userPreferencesCookie);
  }
}
export var initialPreferencesAreMissing = function initialPreferencesAreMissing(initialPreferences) {
  if (!initialPreferences) {
    return true;
  }
  var prefExistButAreEmpty = Object.keys(initialPreferences).length === 0;
  return prefExistButAreEmpty;
};

// The goal here is to transform the initialPreferences into a format we can consume
// and store values in the correct cookies if necessary
// If at anytime, something fails during the process, set to INTERNAL so we can refetch
export var handleInitialPreferencesData = function handleInitialPreferencesData(initialPreferences) {
  if (initialPreferences !== PrefetchedPreferenceState.INTERNAL) {
    var _initialPreferences$c;
    if (initialPreferencesAreMissing(initialPreferences)) {
      // Failsafe handling: Consumer failed to provide data even though they have prefetched, so we need to fetch again
      // This may occur if a consumer's prefetch requests fails, or if they fail to implement the system correctly
      return PrefetchedPreferenceState.INTERNAL;
    }
    // If initialPreferences is not INTERNAL on first render, we have been provided a pre-fetched
    // value, which means we don't need to make a fetch to storage or ConsentHub to get it.
    // If that preference is defined, we persist it to our local cookie set

    // If the data is a ConsentHubResponse/PrefetchedConsentCookieData, we'll need to transform it properly before consumption
    var consentToken = initialPreferences === null || initialPreferences === void 0 ? void 0 : initialPreferences.consentToken;
    var prefetchedLocale = (_initialPreferences$c = initialPreferences === null || initialPreferences === void 0 ? void 0 : initialPreferences.currentLocale) !== null && _initialPreferences$c !== void 0 ? _initialPreferences$c : 'UNKNOWN';

    // If this data comes from prefetching, we'll have no way to capture the existing locale if we don't set here
    setCurrentLocaleCookie(prefetchedLocale);
    if (consentToken) {
      // We already have a consent cookie that was passed in, let's not override it by setting it again
      var unpackedExistingPrefs = unpackUserPreferencesCookie(consentToken);
      return unpackedExistingPrefs !== null && unpackedExistingPrefs !== void 0 ? unpackedExistingPrefs : PrefetchedPreferenceState.INTERNAL;
    }
    var serializedPrefetchedConsentToken = serializeConsents(initialPreferences);
    var unpackedPrefetchedPrefs = unpackUserPreferencesCookie(serializedPrefetchedConsentToken);
    return unpackedPrefetchedPrefs || PrefetchedPreferenceState.INTERNAL;
  }
  // If we've fallen into this, EMPTY implies we have no data at all
  // INTERNAL implies we still need to fetch new data, so trigger a refetch
  return initialPreferences;
};